import React, { useState, useEffect } from "react";
import { useParams } from "react-router";

import AdminDashboardOfferItem from "./AdminDashboardOfferItem";
import { AdminChatBoxType } from "../../chat/AdminChatBox";
import { SonicMessage } from "../../../common/classes/SonicMessage";
import { ChatErrorType } from "../../../common/classes/SonicChatErrorType";

import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { getAuthToken } from "../../../redux/reducers/auth";
import { getPerson, getPersonData } from "../../../redux/reducers/person";

import { getMessagesAdmin } from "../../../services/api/chat/getMessagesAdmin";
import { postCreateChatroomAdmin } from "../../../services/api/chat/postCreateChatroomAdmin";

import { useGetOfferAdmin, useUpdateOfferAdmin } from "../../../utils/useOffer";
import { useGetTeams } from "../../../utils/useTeam";

type Props = {
  isSelect: boolean;
};

const AdminDashboardOffer: React.FC<Props> = ({ isSelect }) => {
  const { id } = useParams<{ id: string }>();

  const recipient = useAppSelector(getPersonData)[0];
  const auth_token = useAppSelector(getAuthToken);
  const dispatch = useAppDispatch();

  const queryKey = {
    auth_token,
    id,
    isSelect,
  };

  const {
    items: { offers, meta },
    isLoading,
  } = useGetOfferAdmin(queryKey);
  const offer = offers[0];

  const { update, updateLoading } = useUpdateOfferAdmin([
    "offerAdmin",
    queryKey,
  ]);

  const { items: { teams } } = useGetTeams({
    id: offer?.recipient_type === "teams" ? offer?.to : null,
  });
  const team = teams[0]; // チームからのオファーは現状ないので不要？

  useEffect(() => {
    if (offer && offer.id === id) {
      fetchMessages();
    }

    if (offer?.recipient_type === "database_entries") {
      const fetchRecipient = dispatch(getPerson({ id: offer?.to }));

      return () => {
        fetchRecipient.abort();
      };
    }
  }, [offer]);

  // Status component
  const [selectStatus, setSelectStatus] = useState<string>();
  const [showStatusMenu, setShowStatusMenu] = useState<boolean>(false);

  const handleUpdateStatus = async () => {
    if (auth_token && id) {
      await update({
        auth_token,
        isSelect,
        offer_id: id,
        status: selectStatus,
      });

      setShowStatusMenu(!showStatusMenu);
    }
  };

  const handleStatusMenu = () => {
    setShowStatusMenu(!showStatusMenu);
  };

  // UserNote component
  const [isMemoEdit, setMemoEdit] = useState<boolean>(false);
  const [memo, setMemo] = useState<string>();

  const handleMemoEdit = () => {
    setMemoEdit(true);
  };

  const handleMemoUpdate = async () => {
    if (auth_token && id) {
      await update({ auth_token, isSelect, offer_id: id, memo: memo });
      setMemoEdit(false);
    }
  };

  // Chat
  const [messages, setMessages] = useState<Array<SonicMessage>>([]);
  const [hasNext, setHasNext] = useState<boolean>(false);
  const [chatboxType, setChatboxType] = useState<AdminChatBoxType>(
    AdminChatBoxType.Loading
  );

  const handleLoad = async (page: number) => {
    if (!auth_token || !hasNext || !id) return;

    const res = await getMessagesAdmin(
      auth_token,
      offer.from,
      offer.recipient_account,
      page,
      isSelect
    );
    if (typeof res !== "object") return;

    setMessages((prevMessages) => [...res.messageHistory, ...prevMessages]);
    setHasNext(res.hasNext);
  };

  const fetchMessages = async () => {
    if (auth_token) {
      const res = await getMessagesAdmin(
        auth_token,
        offer.from,
        offer.recipient_account,
        1,
        isSelect
      );

      // Success
      if (typeof res === "object") {
        setMessages(res.messageHistory);
        setHasNext(res.hasNext);
        setChatboxType(AdminChatBoxType.ShowMessages);
        return;
      }

      // Error
      switch (res) {
        case ChatErrorType.RoomNotFound:
          setChatboxType(AdminChatBoxType.ReCreate);
          break;
        default:
          setChatboxType(AdminChatBoxType.Error);
          break;
      }
      return;
    }
  };

  // ルーム再作成
  const handleReCreateRoom = async () => {
    if (auth_token && offer) {
      setChatboxType(AdminChatBoxType.ReCreating);

      const res = await postCreateChatroomAdmin(
        auth_token,
        offer.from,
        offer.recipient_account,
        "offer",
        offer.to,
        offer.timestamp * 1000
      );

      if (res === null) {
        return setChatboxType(AdminChatBoxType.FailedReCreate);
      }

      return setChatboxType(AdminChatBoxType.ReCreated);
    }
  };

  return (
    <AdminDashboardOfferItem
      isSelect={isSelect}
      isLoading={isLoading || updateLoading}
      offer={offer}
      meta={meta[0]}
      showStatusMenu={showStatusMenu}
      handleUpdateStatus={handleUpdateStatus}
      handleStatusMenu={handleStatusMenu}
      selectStatus={selectStatus}
      setSelectStatus={setSelectStatus}
      isMemoEdit={isMemoEdit}
      handleMemoEdit={handleMemoEdit}
      handleMemoUpdate={handleMemoUpdate}
      setMemo={setMemo}
      recipient={recipient}
      messages={messages}
      hasNext={hasNext}
      handleLoad={handleLoad}
      handleReCreateRoom={handleReCreateRoom}
      chatboxType={chatboxType}
    />
  );
};

export default AdminDashboardOffer;
